/**
 * This file contains logic for managing global error state.
 */

import { showDialog } from '@components/dialog';

type AppErrorState =
  | undefined
  | {
      title?: string;
      error: string | Error;
      message?: string;
      data?: { errors?: Error[]; code?: string };
    };

type ShowArg = Error | AppErrorState;

export const showError = (err: ShowArg) => {
  if (!err) {
    return;
  }
  console.error(err);
  const errState = err as AppErrorState;
  const title =
    errState?.title ||
    (typeof errState?.error === 'string' ? errState.error : 'An unknown error occurred');
  // The server no longer has the endpoint this tab's code called: the site was
  // deployed since the page loaded (server/rpx/index.ts staleClientError).
  const isStaleClient = errState?.data?.code === 'STALE_CLIENT';
  return showDialog({
    mode: 'warn',
    title,
    children: errMessage(err) || '',
    hideCancel: !isStaleClient,
    cancelButtonText: isStaleClient ? 'Not now' : undefined,
    confirmButtonText: isStaleClient ? 'Refresh page' : 'Dismiss',
    // Error dialogs should always be on top.
    zIndex: 'z-max',
  }).then((confirmed) => {
    if (isStaleClient && confirmed) {
      location.reload();
    }
    return confirmed;
  });
};

function errMessage(state: ShowArg) {
  if (!state) {
    return;
  }
  // Look for validation errors, and show the first one,
  // if they exist.
  const appErr = state as AppErrorState;
  const errs = appErr?.data?.errors;

  if (!errs?.length) {
    return state.message || (appErr?.error as Error)?.message || appErr?.error?.toString();
  }

  return errs[0].message;
}
